import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../hooks/useAuth';
import logoOQ from '../../assets/images/logo-light.png';
import './style.css';

interface InstallPWAProps {
  icon: string;
  name: string;
  text: string;
  client: string;
  color: string;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => void;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

export function InstallPWA(props: InstallPWAProps) {
  const { client, getClientByUrl } = useAuth();
  const [pwaState, setPwaState] = useState({
    icon: props.icon,
    name: props.name,
    text: props.text,
    clientUrl: props.client,
    color: props.color,
    supportsPWA: false,
    promptInstall: null as BeforeInstallPromptEvent | null,
    manifestUrl: null as string | null,
    isClose: true,
    messagePointsInstall: '',
  });

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      const beforeInstallEvent = e as BeforeInstallPromptEvent;
      setPwaState((prev) => ({
        ...prev,
        supportsPWA: true,
        promptInstall: beforeInstallEvent,
      }));
    };

    // Listener para mudanças no modo de exibição
    const displayModeHandler = () => {
      if (isPWAInstalled()) {
        setPwaState((prev) => ({ ...prev, isClose: true }));
      }
    };

    getClientByUrl();
    window.addEventListener('beforeinstallprompt', handler);
    window
      .matchMedia('(display-mode: standalone)')
      .addEventListener('change', displayModeHandler);

    setPwaState((prev) => ({
      ...prev,
      isClose: !shouldShowInstallPrompt(),
    }));

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window
        .matchMedia('(display-mode: standalone)')
        .removeEventListener('change', displayModeHandler);
    };
  }, []);

  useEffect(() => {
    if (
      pwaState.icon &&
      pwaState.name &&
      pwaState.clientUrl &&
      pwaState.color
    ) {
      const manifestUrl = generateManifestUrl();
      setPwaState((prev) => ({ ...prev, manifestUrl }));
    }
  }, [pwaState.icon, pwaState.name, pwaState.clientUrl, pwaState.color]);

  useEffect(() => {
    if (client.pointAppIsActive === 'true') {
      const coinName = client?.coinName
        ? client.coinName.replace(/\((\w+)\)/, (_, content) => content)
        : 'pontos';

      setPwaState((prev) => ({
        ...prev,
        messagePointsInstall: `Instale o app e ganhe ${client.pointsDownloadpwa} ${coinName}.`,
      }));
    }
  }, [client]);

  function shouldShowInstallPrompt(): boolean {
    if (isPWAInstalled()) return false;

    const lastShowDate = localStorage.getItem('showInstallDate');
    if (!lastShowDate) return true;

    try {
      const lastDate = new Date(lastShowDate);
      const currentDate = new Date();
      const diffTime = currentDate.getTime() - lastDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return diffDays >= 10;
    } catch (error) {
      console.error('Error parsing dates:', error);
      return true;
    }
  }

  function generateManifestUrl() {
    return `https://oqdigital.com/pwa/manifest.json/${encodeURIComponent(
      window.location.origin
    )}/${pwaState.clientUrl}/${encodeURIComponent(pwaState.name)}/${encodeURIComponent(
      pwaState.icon
    )}/${encodeURIComponent(pwaState.color)}`;
  }

  function isPWAInstalled(): boolean {
    // Verifica de várias formas diferentes para maior confiabilidade
    return (
      // Para Chrome e derivados
      window.matchMedia('(display-mode: standalone)').matches ||
      // Para Safari iOS
      (window.navigator as any).standalone ||
      // Para Android
      document.referrer.includes('android-app://') ||
      // Verificação adicional no localStorage
      localStorage.getItem('pwaInstalled') === 'true'
    );
  }

  async function install(e: React.MouseEvent) {
    e.preventDefault();
    if (!pwaState.promptInstall) return;

    try {
      pwaState.promptInstall.prompt();
      const { outcome } = await pwaState.promptInstall.userChoice;

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        // You can add analytics tracking here
        localStorage.setItem('pwaInstalled', 'true');
      } else {
        console.log('User dismissed the install prompt');
      }

      setPwaState((prev) => ({ ...prev, isClose: true }));
    } catch (error) {
      console.error('Installation failed:', error);
    }
  }

  function closeBox(e: React.MouseEvent) {
    e.preventDefault();
    localStorage.setItem('showInstallDate', new Date().toString());
    setPwaState((prev) => ({ ...prev, isClose: true }));
  }

  function getInstallMessage() {
    if (!pwaState.supportsPWA) {
      return 'Seu navegador não suporta instalação de PWA.';
    }
    return (
      pwaState.messagePointsInstall ||
      'Instale nosso aplicativo para uma melhor experiência.'
    );
  }

  return (
    <>
      <Helmet>
        {pwaState.manifestUrl && (
          <link rel="manifest" href={pwaState.manifestUrl} />
        )}
      </Helmet>

      {!pwaState.isClose && (
        <div
          className={`install-pwa-wrapper animate__animated animate__slideInUp`}
        >
          <div className="install-pwa-box">
            <div className="content-header">
              <button
                className="install-pwa-close"
                onClick={closeBox}
                aria-label="Fechar banner de instalação"
              >
                Me lembre depois
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="content-top">
              <div className="box-image">
                <img
                  src={pwaState.icon || ''}
                  alt={`Ícone do aplicativo ${pwaState.name}`}
                />
              </div>
              <div>
                <p className="truncate">{pwaState.name || ''}</p>
                <div className="content-group-h">
                  <img
                    width="16"
                    height="16"
                    style={{ marginRight: '10px' }}
                    src={logoOQ}
                    alt="Logo OQ Digital"
                  />
                  <span>Powered by OQ Digital</span>
                </div>
                <span>{getInstallMessage()}</span>
              </div>
            </div>
            <div className="content-bottom">
              <div className="content-left">
                <div className="content-group-h">
                  <i className="material-icons" style={{ marginRight: '10px' }}>
                    install_mobile
                  </i>
                  <span style={{ fontSize: '20px' }}>Baixar app</span>
                </div>
              </div>
              <div className="content-right">
                <button
                  className="btn-install animate__animated animate__headShake animate__delay-2s"
                  onClick={install}
                  disabled={!pwaState.supportsPWA}
                  aria-label="Instalar aplicativo"
                >
                  Instalar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
